import { Check, MapPin, PartyPopper, Plus, Users } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/** Client-only demo beats for the Circle onboarding step. No network / DB. */
export const CIRCLE_PREVIEW_STEPS = 5;

type CircleTourPreviewProps = {
  step: number;
  className?: string;
};

function ExampleBadge({ label }: { label: string }) {
  return (
    <span className="rounded-full border border-[#D4A84B]/35 bg-[#D4A84B]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#D4A84B]">
      {label}
    </span>
  );
}

function MiniVenueRow({
  title,
  subtitle,
  action,
  highlighted,
}: {
  title: string;
  subtitle: string;
  action?: ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-xl border px-3 py-2.5",
        highlighted
          ? "border-[#D4A84B]/45 bg-[#D4A84B]/10"
          : "border-white/10 bg-white/[0.03]",
      )}
    >
      <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/10 text-white/70">
        <MapPin className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">{title}</p>
        <p className="truncate text-[11px] text-white/45">{subtitle}</p>
      </div>
      {action}
    </div>
  );
}

function PreviewShell({
  caption,
  exampleLabel,
  children,
}: {
  caption: string;
  exampleLabel: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs leading-relaxed text-white/60">{caption}</p>
        <ExampleBadge label={exampleLabel} />
      </div>
      <div className="rounded-2xl border border-white/10 bg-[#0B0B0C]/80 p-3">{children}</div>
    </div>
  );
}

function VotePreview({
  club,
  bar,
  friend,
  youLabel,
  votesLabel,
  winnerLabel,
}: {
  club: string;
  bar: string;
  friend: string;
  youLabel: string;
  votesLabel: string;
  winnerLabel: string;
}) {
  const [phase, setPhase] = useState<"idle" | "friend" | "result">("idle");

  useEffect(() => {
    const t1 = window.setTimeout(() => setPhase("friend"), 700);
    const t2 = window.setTimeout(() => setPhase("result"), 1600);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, []);

  return (
    <div className="space-y-2">
      <div
        className={cn(
          "rounded-xl border px-3 py-2.5 transition-colors",
          phase === "result"
            ? "border-[#D4A84B]/50 bg-[#D4A84B]/15"
            : "border-white/10 bg-white/[0.03]",
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-white">{club}</p>
          <span className="text-[11px] text-white/55">
            {phase === "idle" ? `1 ${votesLabel}` : `2 ${votesLabel}`}
          </span>
        </div>
        <div className="mt-2 flex gap-1.5">
          <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] text-white/80">
            {youLabel}
          </span>
          {phase !== "idle" ? (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[10px] text-white/80">
              {friend}
            </span>
          ) : null}
        </div>
        {phase === "result" ? (
          <p className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[#D4A84B]">
            <Check className="h-3.5 w-3.5" />
            {winnerLabel}
          </p>
        ) : null}
      </div>
      <div className="rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5 opacity-60">
        <div className="flex items-center justify-between gap-2">
          <p className="text-sm font-semibold text-white">{bar}</p>
          <span className="text-[11px] text-white/55">0 {votesLabel}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Purely local mock UI for the Circle tour step.
 * Must never call Supabase / fetch — demo names and React state only.
 */
export function CircleTourPreview({ step, className }: CircleTourPreviewProps) {
  const { t } = useTranslation("consumer");
  const clamped = Math.max(0, Math.min(CIRCLE_PREVIEW_STEPS - 1, step));
  const example = t("onboarding.tour.circlePreview.example");

  return (
    <div className={cn("space-y-3", className)} data-circle-tour-preview="">
      <div className="flex items-center gap-1.5">
        {Array.from({ length: CIRCLE_PREVIEW_STEPS }, (_, i) => (
          <span
            key={i}
            className={cn(
              "h-1 flex-1 rounded-full transition-colors",
              i <= clamped ? "bg-[#D4A84B]" : "bg-white/15",
            )}
          />
        ))}
      </div>

      {clamped === 0 ? (
        <PreviewShell
          caption={t("onboarding.tour.circlePreview.steps.venue.caption")}
          exampleLabel={example}
        >
          <MiniVenueRow
            title={t("onboarding.tour.circlePreview.names.club")}
            subtitle={t("onboarding.tour.circlePreview.names.clubMeta")}
            highlighted
            action={
              <span className="inline-flex items-center gap-1 rounded-full bg-[#D4A84B] px-2.5 py-1 text-[11px] font-semibold text-[#0B0B0C]">
                <Plus className="h-3 w-3" />
                {t("onboarding.tour.circlePreview.steps.venue.add")}
              </span>
            }
          />
        </PreviewShell>
      ) : null}

      {clamped === 1 ? (
        <PreviewShell
          caption={t("onboarding.tour.circlePreview.steps.night.caption")}
          exampleLabel={example}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-3 py-2.5">
              <PartyPopper className="h-4 w-4 text-[#D4A84B]" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white">
                  {t("onboarding.tour.circlePreview.names.night")}
                </p>
                <p className="text-[11px] text-white/45">
                  {t("onboarding.tour.circlePreview.steps.night.meta")}
                </p>
              </div>
            </div>
            <MiniVenueRow
              title={t("onboarding.tour.circlePreview.names.club")}
              subtitle={t("onboarding.tour.circlePreview.names.clubMeta")}
            />
          </div>
        </PreviewShell>
      ) : null}

      {clamped === 2 ? (
        <PreviewShell
          caption={t("onboarding.tour.circlePreview.steps.second.caption")}
          exampleLabel={example}
        >
          <div className="space-y-2">
            <p className="text-[11px] font-medium text-white/50">
              {t("onboarding.tour.circlePreview.names.night")}
            </p>
            <MiniVenueRow
              title={t("onboarding.tour.circlePreview.names.club")}
              subtitle={t("onboarding.tour.circlePreview.names.clubMeta")}
            />
            <MiniVenueRow
              title={t("onboarding.tour.circlePreview.names.bar")}
              subtitle={t("onboarding.tour.circlePreview.names.barMeta")}
              highlighted
            />
          </div>
        </PreviewShell>
      ) : null}

      {clamped === 3 ? (
        <PreviewShell
          caption={t("onboarding.tour.circlePreview.steps.invite.caption")}
          exampleLabel={example}
        >
          <div className="flex items-center gap-3 rounded-xl border border-[#D4A84B]/35 bg-[#D4A84B]/10 px-3 py-3">
            <div className="grid h-10 w-10 place-items-center rounded-full bg-white/10">
              <Users className="h-4 w-4 text-[#D4A84B]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">
                {t("onboarding.tour.circlePreview.names.friend")}
              </p>
              <p className="text-[11px] text-white/55">
                {t("onboarding.tour.circlePreview.steps.invite.joined")}
              </p>
            </div>
            <Check className="h-4 w-4 text-[#D4A84B]" />
          </div>
        </PreviewShell>
      ) : null}

      {clamped === 4 ? (
        <PreviewShell
          caption={t("onboarding.tour.circlePreview.steps.vote.caption")}
          exampleLabel={example}
        >
          <VotePreview
            club={t("onboarding.tour.circlePreview.names.club")}
            bar={t("onboarding.tour.circlePreview.names.bar")}
            friend={t("onboarding.tour.circlePreview.names.friend")}
            youLabel={t("onboarding.tour.circlePreview.steps.vote.you")}
            votesLabel={t("onboarding.tour.circlePreview.steps.vote.votes")}
            winnerLabel={t("onboarding.tour.circlePreview.steps.vote.winner")}
          />
        </PreviewShell>
      ) : null}
    </div>
  );
}
